import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, Calendar, Receipt, Mail, RefreshCw, CheckCircle2, Clock, XCircle } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { fetchTransactions, SUPPORT_EMAIL } from "../lib/billing";
import { useEntitlement } from "../contexts/EntitlementContext";
import axios from "axios";
import { getToken } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function planLabel(plan) {
  return plan === "yearly" ? "Anual" : plan === "monthly" ? "Mensual" : plan || "—";
}

function StatusPill({ status, paymentStatus, credited }) {
  if (credited || paymentStatus === "paid") {
    return (
      <span className="inline-flex items-center gap-1 bg-[#E8F9EE] text-[#1A6B2F] text-xs font-bold px-2.5 py-1 rounded-full">
        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={3} />
        Pagado
      </span>
    );
  }
  if (status === "expired") {
    return (
      <span className="inline-flex items-center gap-1 bg-[#FFEAEA] text-[#803535] text-xs font-bold px-2.5 py-1 rounded-full">
        <XCircle className="w-3.5 h-3.5" strokeWidth={3} />
        Expirado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-[#FFF6CC] text-[#7A5C00] text-xs font-bold px-2.5 py-1 rounded-full">
      <Clock className="w-3.5 h-3.5" strokeWidth={3} />
      Pendiente
    </span>
  );
}

export default function MiSuscripcion() {
  const navigate = useNavigate();
  const { entitlement, refresh } = useEntitlement();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const [recoverMsg, setRecoverMsg] = useState("");

  const loadTxs = async () => {
    try {
      const data = await fetchTransactions();
      setTxs(data);
    } catch {
      setTxs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    /* fetch initial data on mount */
    loadTxs();
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRecover = async () => {
    setRecovering(true);
    setRecoverMsg("");
    try {
      const { data } = await axios.post(
        `${API}/billing/recover`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (data.credited > 0) {
        setRecoverMsg(`🎉 ¡Se confirmó tu pago! Tu cuenta ya es Premium.`);
        refresh();
        loadTxs();
      } else if (data.checked === 0) {
        setRecoverMsg("No hay pagos pendientes en tu cuenta.");
      } else {
        setRecoverMsg(
          "Aún no se confirma tu pago en Stripe. Espera un par de minutos y vuelve a intentar. Si el problema continúa, escríbenos a " +
            SUPPORT_EMAIL +
            "."
        );
      }
    } catch (e) {
      setRecoverMsg("No pudimos verificar tu pago. Intenta de nuevo en un minuto.");
    } finally {
      setRecovering(false);
    }
  };

  const premiumActive = !!entitlement?.premium;
  const trialActive = !!entitlement?.trial_active;
  const premiumUntil = entitlement?.premium_until;

  const refundMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    "Solicitud de reembolso — MateConMar"
  )}&body=${encodeURIComponent(
    `Hola,\n\nQuiero solicitar un reembolso de mi pago en MateConMar.\n\nUsuario: \nFecha de pago: \nMotivo: \n\nGracias.`
  )}`;

  const supportMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    "Soporte — MateConMar"
  )}`;

  return (
    <PageShell>
      <div className="mt-4">
        <ClayButton variant="ghost" onClick={() => navigate("/dashboard")} data-testid="mi-sus-back-btn">
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto mt-4"
      >
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-6" data-testid="mi-sus-title">
          Mi Suscripción
        </h1>

        {/* Current status card */}
        <div
          className={`rounded-3xl border-4 p-5 sm:p-6 mb-6 ${
            premiumActive
              ? "bg-gradient-to-br from-[#FFF6CC] to-white border-[#FFD700]"
              : trialActive
              ? "bg-gradient-to-br from-[#E8F9EE] to-white border-[#34C759]"
              : "bg-white border-gray-100"
          }`}
          data-testid="mi-sus-status-card"
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                premiumActive
                  ? "bg-gradient-to-br from-[#FFD700] to-[#FF9500]"
                  : trialActive
                  ? "bg-[#34C759]"
                  : "bg-gray-100"
              }`}
            >
              <Crown
                className={`w-7 h-7 ${premiumActive || trialActive ? "text-white" : "text-gray-400"}`}
                strokeWidth={2.5}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-xl sm:text-2xl font-bold text-[#1C1C1E]" data-testid="mi-sus-status-label">
                {premiumActive ? "Premium activo" : trialActive ? "Prueba gratis activa" : "Sin suscripción"}
              </p>
              {premiumActive && premiumUntil && (
                <p className="text-sm text-[#5C5C60] mt-1 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" strokeWidth={2.5} />
                  Vence el <strong className="text-[#1C1C1E]">{formatDate(premiumUntil)}</strong>
                </p>
              )}
              {trialActive && (
                <p className="text-sm text-[#5C5C60] mt-1">
                  Te quedan <strong className="text-[#1A6B2F]">{entitlement.trial_days_left} día{entitlement.trial_days_left === 1 ? "" : "s"}</strong> de prueba gratis.
                </p>
              )}
              {!premiumActive && !trialActive && (
                <p className="text-sm text-[#5C5C60] mt-1">
                  Estás en el plan gratis con 1 ronda al día. Hazte Premium para practicar sin límites.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-5">
            {!premiumActive && (
              <ClayButton onClick={() => navigate("/upgrade")} data-testid="mi-sus-upgrade-btn">
                <Crown className="w-5 h-5" strokeWidth={3} />
                {trialActive ? "Hazte Premium" : "Suscribirme"}
              </ClayButton>
            )}
            {premiumActive && (
              <ClayButton variant="secondary" onClick={() => navigate("/upgrade")} data-testid="mi-sus-renew-btn">
                <RefreshCw className="w-5 h-5" strokeWidth={3} />
                Renovar / Extender
              </ClayButton>
            )}
            {txs.some((t) => !t.credited) && !premiumActive && (
              <ClayButton
                variant="secondary"
                onClick={handleRecover}
                disabled={recovering}
                data-testid="mi-sus-recover-btn"
              >
                <RefreshCw className={`w-5 h-5 ${recovering ? "animate-spin" : ""}`} strokeWidth={3} />
                {recovering ? "Verificando…" : "Verificar pago"}
              </ClayButton>
            )}
          </div>
          {recoverMsg && (
            <div
              className="mt-3 text-sm font-bold text-[#1A6B2F] bg-[#E8F9EE] border-2 border-[#34C759] rounded-2xl px-4 py-3"
              data-testid="mi-sus-recover-msg"
            >
              {recoverMsg}
            </div>
          )}

          {premiumActive && (
            <p className="text-xs text-[#5C5C60] mt-3">
              Tu Premium es de pago único (no se renueva automáticamente). Puedes renovar manualmente antes o después del vencimiento.
            </p>
          )}
        </div>

        {/* Payments history */}
        <div className="bg-white rounded-3xl border-4 border-white shadow-md p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="w-5 h-5 text-[#5C5C60]" strokeWidth={2.5} />
            <h2 className="font-heading text-xl font-bold text-[#1C1C1E]">
              Historial de pagos
            </h2>
          </div>

          {loading && (
            <p className="text-sm text-[#5C5C60]" data-testid="mi-sus-loading">
              Cargando…
            </p>
          )}

          {!loading && txs.length === 0 && (
            <p className="text-sm text-[#5C5C60]" data-testid="mi-sus-empty">
              Aún no hay pagos registrados.
            </p>
          )}

          {!loading && txs.length > 0 && (
            <ul className="divide-y divide-gray-100" data-testid="mi-sus-tx-list">
              {txs.map((tx) => (
                <li key={tx.session_id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-[#1C1C1E]">
                      Plan {planLabel(tx.plan)} · ${tx.amount} {(tx.currency || "MXN").toUpperCase()}
                    </p>
                    <p className="text-xs text-[#5C5C60]">{formatDate(tx.created_at)}</p>
                  </div>
                  <StatusPill
                    status={tx.status}
                    paymentStatus={tx.payment_status}
                    credited={tx.credited}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Support / cancel / refund */}
        <div className="bg-white rounded-3xl border-4 border-white shadow-md p-5 sm:p-6">
          <h2 className="font-heading text-xl font-bold text-[#1C1C1E] mb-2">
            Cancelar o pedir reembolso
          </h2>
          <p className="text-sm text-[#5C5C60] mb-4">
            Tu Premium es de pago único — no hay cargos recurrentes, así que no tienes que cancelar nada para evitar futuros cobros.
            Si quieres pedir un reembolso, tienes <strong>7 días</strong> a partir del pago. Escríbenos:
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={refundMailto}
              className="inline-flex items-center justify-center gap-2 bg-[#FFEAEA] hover:bg-[#FFD9D9] border-2 border-[#FF6B6B] text-[#803535] font-bold rounded-full px-5 py-3 transition-colors"
              data-testid="mi-sus-refund-btn"
            >
              <Mail className="w-5 h-5" strokeWidth={3} />
              Solicitar reembolso
            </a>
            <a
              href={supportMailto}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border-2 border-gray-200 text-[#1C1C1E] font-bold rounded-full px-5 py-3 transition-colors"
              data-testid="mi-sus-support-btn"
            >
              <Mail className="w-5 h-5" strokeWidth={3} />
              Soporte general
            </a>
          </div>
          <p className="text-xs text-[#5C5C60] mt-4">
            Lee los <button onClick={() => navigate("/terminos")} className="underline font-bold" data-testid="mi-sus-terms-link">Términos y Política de Reembolso</button>.
          </p>
        </div>
      </motion.div>
    </PageShell>
  );
}
